/**
 * Per-pool derived data the controller computes on the fly + the
 * pool-scoped workload draft / live hooks.
 *
 * The placements lookup materialises the Provider config inheritance
 * chain (Global / Service / Pool override) the same way the renderer
 * would at provisioning time — Pools no longer carry a placements
 * column of their own.
 *
 * The workload hooks mirror the service-level draft / promote
 * protocol from `@/api/local/workload` but operate on ``Pool.workload``
 * / ``Pool.workload_draft``. A Pool can be in one of two modes:
 *   - **inherit**: ``pool.workload`` is NULL; the pool runs whatever
 *     ``Service.workload`` has. The editor renders read-only.
 *   - **own**: the pool has detached. ``pool.workload`` (or a pending
 *     ``pool.workload_draft``) overrides ``Service.workload`` for this
 *     pool only. The editor binds to the draft; rollouts (or the
 *     `/promote` endpoint) flip it to live.
 *
 * Cached aggressively (1 min) because the inputs only change when the
 * operator edits providers / workloads — invalidations are explicit
 * on the matching mutations.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  Pool,
  PoolProvisioningError,
  PoolReplicaError,
  PoolWorkloadOut,
  ResolvedPlacement,
  ResourceCatalog,
  WorkloadSpec,
} from "@/api/types";

/**
 * POST /pools/:id/reset — operator-initiated state-machine reset.
 *
 * Drops every retry / attention / status-lost counter, deletes any
 * orphan replica rows, and parks the pool at `pending` so the next
 * reconcile tick re-enters `sky.serve.up` on a clean slate. Used by
 * the PhaseCard "Reset pool state" action when the pool is parked in
 * `needs_attention` / `retrying` and the operator has fixed the
 * underlying cause.
 */
export function resetPool(poolId: number): Promise<Pool> {
  return api.post<Pool>(`/pools/${poolId}/reset`);
}

const PLACEMENTS_EMPTY: ReadonlyArray<ResolvedPlacement> = Object.freeze([]);

/**
 * Resolved placements for one Pool. Returns the empty array (cached
 * frozen instance) until the request settles or when the pool has no
 * provider enabled yet — call sites can render an "unconfigured"
 * affordance off ``placements.length === 0`` without an extra
 * loading state.
 */
export function usePoolPlacements(
  poolId: number | undefined,
): ReadonlyArray<ResolvedPlacement> {
  const q = useQuery({
    queryKey: ["pools", poolId, "placements"],
    queryFn: () =>
      api.get<ResolvedPlacement[]>(
        `/pools/${poolId}/placements`,
      ),
    enabled: poolId != null,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  return q.data ?? PLACEMENTS_EMPTY;
}

/**
 * Pool Spec picker payload — every GPU/CPU shape SkyPilot's catalog
 * knows about, with per-cloud availability annotated for this pool's
 * Provider inheritance chain. Cached aggressively (5 min) because the
 * inputs (SkyPilot's local catalog + the operator's enabled
 * providers) change rarely; the controller refreshes the catalog on
 * its own ~8h cycle and provider edits invalidate via
 * ``["pools", id, "resource-catalog"]``.
 */
export function usePoolResourceCatalog(poolId: number | undefined) {
  return useQuery({
    queryKey: ["pools", poolId, "resource-catalog"],
    enabled: poolId != null,
    queryFn: () =>
      api.get<ResourceCatalog>(`/pools/${poolId}/resource-catalog`),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

const PROVISIONING_ERRORS_EMPTY: ReadonlyArray<PoolProvisioningError> =
  Object.freeze([]);

/**
 * Recent provisioning errors scoped to one Pool — drives the "why is
 * this pool stuck at 0/0?" callout on the Replicas tab. The endpoint
 * is bounded by `since_minutes` server-side so the callout naturally
 * clears once the operator has fixed the spec and a fresh reconcile
 * tick has succeeded; we still poll on a 15s cadence so a transient
 * `provisioning.no_capacity` shows up promptly.
 *
 * Returns the frozen empty array (not a fresh array literal) before
 * the request settles so consumers can `.length === 0` without
 * tripping render loops on referential identity.
 */
export function usePoolProvisioningErrors(
  poolId: number | undefined,
): ReadonlyArray<PoolProvisioningError> {
  const q = useQuery({
    queryKey: ["pools", poolId, "provisioning-errors"],
    enabled: poolId != null,
    queryFn: () =>
      api.get<PoolProvisioningError[]>(
        `/pools/${poolId}/provisioning-errors`,
      ),
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
  return q.data ?? PROVISIONING_ERRORS_EMPTY;
}

const REPLICA_ERRORS_EMPTY: ReadonlyArray<PoolReplicaError> =
  Object.freeze([]);

/**
 * Recent classified user-app failures scoped to one Pool — drives the
 * "all replicas crash on alembic / missing module / etc." callout on
 * the Replicas tab. Companion of `usePoolProvisioningErrors`; sister
 * cadence (10s stale, 15s refetch) so a freshly-fixed image converges
 * on a clear callout within a single reconcile tick.
 */
export function usePoolReplicaErrors(
  poolId: number | undefined,
): ReadonlyArray<PoolReplicaError> {
  const q = useQuery({
    queryKey: ["pools", poolId, "replica-errors"],
    enabled: poolId != null,
    queryFn: () =>
      api.get<PoolReplicaError[]>(`/pools/${poolId}/replica-errors`),
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
  return q.data ?? REPLICA_ERRORS_EMPTY;
}

const workloadKey = (poolId: number) =>
  ["pools", poolId, "workload"] as const;

/**
 * Fetch the pool-scoped workload state (mode + live + draft +
 * optimistic-lock revision). The same query is keyed off the pool id
 * so concurrent callers across the page (header badge, editor, save
 * button) all read from one cache entry.
 */
export function usePoolWorkload(poolId: number | undefined) {
  return useQuery({
    queryKey: workloadKey(poolId ?? -1),
    enabled: poolId != null,
    queryFn: () =>
      api.get<PoolWorkloadOut>(`/pools/${poolId}/workload`),
    staleTime: 5_000,
  });
}

/**
 * Common cache-busting after any pool-workload mutation. We invalidate
 * both the dedicated workload query *and* the parent pools list (the
 * Pool row carries `workload` / `workload_draft` / `workload_draft_revision`
 * so a stale list response would lie about the pool's current mode).
 */
function invalidatePoolWorkload(
  queryClient: ReturnType<typeof useQueryClient>,
  poolId: number,
  serviceId?: number | undefined,
) {
  queryClient.invalidateQueries({ queryKey: workloadKey(poolId) });
  queryClient.invalidateQueries({ queryKey: ["pools", "all"] });
  if (serviceId !== undefined) {
    queryClient.invalidateQueries({
      queryKey: ["service", serviceId, "pools"],
    });
  }
}

/** PUT /pools/:id/workload/draft — save the operator's pending edits. */
export function useSavePoolDraftWorkload(
  poolId: number,
  serviceId?: number,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      spec,
      ifRevision,
    }: {
      spec: WorkloadSpec;
      ifRevision?: number;
    }) =>
      api.put<PoolWorkloadOut>(
        `/pools/${poolId}/workload/draft`,
        spec,
        ifRevision !== undefined
          ? { headers: { "If-Match": String(ifRevision) } }
          : undefined,
      ),
    onSuccess: () => invalidatePoolWorkload(queryClient, poolId, serviceId),
  });
}

/** POST /pools/:id/workload/draft/discard — revert pool draft to live. */
export function useDiscardPoolDraftWorkload(
  poolId: number,
  serviceId?: number,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<PoolWorkloadOut>(`/pools/${poolId}/workload/draft/discard`),
    onSuccess: () => invalidatePoolWorkload(queryClient, poolId, serviceId),
  });
}

/**
 * POST /pools/:id/workload/detach — flip from inherit to own.
 *
 * Idempotent on the controller side; on the *first* detach the
 * controller seeds `pool.workload_draft` with a deep copy of the
 * current `Service.workload` so the editor opens with a realistic
 * starting point.
 */
export function useDetachPoolWorkload(
  poolId: number,
  serviceId?: number,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<PoolWorkloadOut>(`/pools/${poolId}/workload/detach`),
    onSuccess: () => invalidatePoolWorkload(queryClient, poolId, serviceId),
  });
}

/**
 * POST /pools/:id/workload/inherit — flip back to inherit, clearing
 * both the pool's live and draft specs. The reconciler picks up the
 * change next tick and re-renders the SkyServe service.yaml with
 * `Service.workload`.
 */
export function useInheritPoolWorkload(
  poolId: number,
  serviceId?: number,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<PoolWorkloadOut>(`/pools/${poolId}/workload/inherit`),
    onSuccess: () => invalidatePoolWorkload(queryClient, poolId, serviceId),
  });
}

/** POST /pools/:id/workload/promote — promote pool draft to live without a rollout. */
export function usePromotePoolDraftWorkload(
  poolId: number,
  serviceId?: number,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<PoolWorkloadOut>(`/pools/${poolId}/workload/promote`),
    onSuccess: () => invalidatePoolWorkload(queryClient, poolId, serviceId),
  });
}
